import React from 'react'
import { Table, Button, Space, Typography, Switch, Tooltip } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import type { DragEndEvent } from '@dnd-kit/core'
import { FlattenedRoomData } from './types'
import { renderDescriptionPreview, renderRoomThumb, renderTagsPreviewWithPopover } from './utils'
import { SortableRow, DragHandle } from './SortableRow'

const { Text } = Typography

type RoomTabletTableProps = {
  rooms: FlattenedRoomData[]
  loading: boolean
  updatingRoomCode: string | null
  isSortMode: boolean
  onShowDetail: (room: FlattenedRoomData) => void
  onEdit: (room: FlattenedRoomData) => void
  onToggleEnabled: (room: FlattenedRoomData, enabled: boolean) => void
  onDragEnd: (event: DragEndEvent) => void
}

const RoomTabletTable: React.FC<RoomTabletTableProps> = ({
  rooms,
  loading,
  updatingRoomCode,
  isSortMode,
  onShowDetail,
  onEdit,
  onToggleEnabled,
  onDragEnd,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 1 } }),
  )

  const dragHandleColumn = {
    key: 'sort',
    width: 40,
    align: 'center' as const,
    render: () => <DragHandle />,
  }

  const dataColumns = [
    {
      title: 'Room',
      key: 'room',
      width: 250,
      fixed: 'left' as const,
      render: (_: any, record: FlattenedRoomData) => (
        <Space>
          {renderRoomThumb(record, onShowDetail)}
          <Space orientation="vertical" size={0} style={{ maxWidth: 190 }}>
            <Text strong ellipsis={{ tooltip: record.display_name }} style={{ maxWidth: '100%' }}>{record.display_name}</Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>{record.room_code}</Text>
            <Text ellipsis style={{ fontSize: '12px', width: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {renderDescriptionPreview(record.description)}
            </Text>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Facilities & Highlights',
      key: 'summary',
      width: 280,
      render: (_: any, record: FlattenedRoomData) => {
        const { highlights, amenities } = record

        return (
          <Space orientation="vertical" size="small" style={{ maxWidth: '100%' }}>
            {renderTagsPreviewWithPopover(highlights, 'geekblue', 2)}
            {renderTagsPreviewWithPopover(amenities, 'green', 3)}
          </Space>
        )
      },
    },
    {
      title: 'Avail',
      children: [
        {
          title: 'Max',
          dataIndex: 'max_avail',
          key: 'max_avail',
          width: 60,
          align: 'center' as const,
          render: (v: number | null | undefined) => v != null ? v : '-',
        },
        {
          title: 'Offset',
          dataIndex: 'avail_offset',
          key: 'avail_offset',
          width: 60,
          align: 'center' as const,
          render: (v: number | null | undefined) => v != null ? v : '-',
        },
      ],
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 80,
      align: 'center' as const,
      render: (_: any, record: FlattenedRoomData) => (
        <Tooltip title={record.enabled ? 'Disable Room' : 'Enable Room'}>
          <Switch
            checked={record.enabled}
            onChange={(checked) => onToggleEnabled(record, checked)}
            loading={updatingRoomCode === record.room_code}
            size="small"
            style={{ width: 40 }}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      align: 'center' as const,
      fixed: 'right' as const,
      render: (_: any, record: FlattenedRoomData) => (
        <Tooltip title="Edit">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => onEdit(record)}
          />
        </Tooltip>
      ),
    },
  ]

  const columns = isSortMode ? [dragHandleColumn, ...dataColumns] : dataColumns

  const table = (
    <Table
      columns={columns}
      dataSource={rooms}
      rowKey="room_code"
      size="small"
      loading={loading}
      scroll={{ x: isSortMode ? 850 : 810 }}
      pagination={isSortMode ? false : { pageSize: 10 }}
      components={isSortMode ? { body: { row: SortableRow } } : undefined}
    />
  )

  if (!isSortMode) return table

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <SortableContext items={rooms.map(r => r.room_code)} strategy={verticalListSortingStrategy}>
        {table}
      </SortableContext>
    </DndContext>
  )
}

export default RoomTabletTable
